import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Users, Radio, Target, DollarSign, Shield, CheckCircle, XCircle,
  AlertTriangle, BarChart3, Eye, Ban, Trash2, Search, ChevronRight,
  Bell, Settings, Flag, Video, UserCheck, Clock
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPanel = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [streams, setStreams] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [ads, setAds] = useState([]);
  const [reports, setReports] = useState([]);
  const [verificationApps, setVerificationApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
    else if (activeTab === 'streams') fetchStreams();
    else if (activeTab === 'advertisers') fetchAdvertisers();
    else if (activeTab === 'ads') fetchAds();
    else if (activeTab === 'reports') fetchReports();
    else if (activeTab === 'verification') fetchVerificationApps();
  }, [activeTab]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users${searchQuery ? `?search=${searchQuery}` : ''}`);
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchStreams = async () => {
    try {
      const response = await axios.get(`${API}/admin/streams`);
      setStreams(response.data.streams);
    } catch (error) {
      console.error('Error fetching streams:', error);
    }
  };

  const fetchAdvertisers = async () => {
    try {
      const response = await axios.get(`${API}/admin/advertisers`);
      setAdvertisers(response.data.advertisers);
    } catch (error) {
      console.error('Error fetching advertisers:', error);
    }
  };

  const fetchAds = async () => {
    try {
      const response = await axios.get(`${API}/admin/ads`);
      setAds(response.data.ads);
    } catch (error) {
      console.error('Error fetching ads:', error);
    }
  };

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/admin/reports`);
      setReports(response.data.reports);
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
  };

  const fetchVerificationApps = async () => {
    try {
      const response = await axios.get(`${API}/admin/verification/applications`);
      setVerificationApps(response.data);
    } catch (error) {
      console.error('Error fetching verification apps:', error);
    }
  };

  const handleUserAction = async (userId, action, value) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, null, {
        params: { [action]: value }
      });
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleTerminateStream = async (streamId) => {
    if (!window.confirm('Are you sure you want to terminate this stream?')) return;
    try {
      await axios.post(`${API}/admin/streams/${streamId}/terminate`);
      fetchStreams();
    } catch (error) {
      console.error('Error terminating stream:', error);
    }
  };

  const handleAdvertiserStatus = async (advertiserId, status) => {
    try {
      await axios.put(`${API}/admin/advertisers/${advertiserId}`, null, {
        params: { status }
      });
      fetchAdvertisers();
    } catch (error) {
      console.error('Error updating advertiser:', error);
    }
  };

  const handleAdStatus = async (adId, status) => {
    try {
      await axios.put(`${API}/admin/ads/${adId}`, null, {
        params: { status }
      });
      fetchAds();
    } catch (error) {
      console.error('Error updating ad:', error);
    }
  };

  const handleVerification = async (applicationId, approved) => {
    try {
      await axios.put(`${API}/admin/verification/${applicationId}`, null, {
        params: { approved }
      });
      fetchVerificationApps();
    } catch (error) {
      console.error('Error processing verification:', error);
    }
  };

  const handleResolveReport = async (reportId, resolution) => {
    try {
      await axios.put(`${API}/admin/reports/${reportId}`, null, {
        params: { resolution }
      });
      fetchReports();
    } catch (error) {
      console.error('Error resolving report:', error);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'streams', label: 'Streams', icon: Radio },
    { id: 'advertisers', label: 'Advertisers', icon: Target },
    { id: 'ads', label: 'Ads', icon: Eye },
    { id: 'reports', label: 'Reports', icon: Flag },
    { id: 'verification', label: 'Verification', icon: UserCheck }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        className="bg-[#08080C] rounded-3xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col border border-white/10"
        data-testid="admin-panel"
      >
        {/* Header */}
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-[#FF2A2A] to-[#FFE800] rounded-2xl flex items-center justify-center shadow-lg shadow-[#FF2A2A]/20">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-black tracking-tight text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Admin Panel
              </h2>
              <p className="text-sm text-gray-500">Platform Management</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar */}
          <div className="w-56 border-r border-white/10 p-4 space-y-2 bg-[#050507]">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  activeTab === tab.id
                    ? 'bg-[#00F0FF]/10 border border-[#00F0FF]/20 text-[#00F0FF]'
                    : 'hover:bg-white/5 text-gray-400 hover:text-white'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span className="font-medium text-sm">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 bg-[#030305]">
            {activeTab === 'overview' && stats && (
              <div className="space-y-4 sm:space-y-6">
                <h3 className="text-base sm:text-lg font-semibold text-white">Platform Overview</h3>
                
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                  <StatCard title="Total Users" value={stats.users?.total || 0} icon={Users} color="#00F0FF" />
                  <StatCard title="Verified Streamers" value={stats.users?.verified_streamers || 0} icon={UserCheck} color="#00FF66" />
                  <StatCard title="Active Streams" value={stats.streams?.active || 0} icon={Radio} color="#FF2A2A" />
                  <StatCard title="Total Streams" value={stats.streams?.total || 0} icon={Video} color="#FFE800" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                  <StatCard title="Advertisers" value={stats.advertisers?.total || 0} subtitle={`${stats.advertisers?.pending || 0} pending`} icon={Target} color="#FFE800" />
                  <StatCard title="Platform Revenue (Tips)" value={`$${(stats.revenue?.platform_tip_revenue || 0).toFixed(2)}`} icon={DollarSign} color="#00FF66" />
                  <StatCard title="Ad Revenue" value={`$${(stats.revenue?.total_ad_revenue || 0).toFixed(2)}`} icon={DollarSign} color="#00F0FF" />
                </div>
              </div>
            )}

            {activeTab === 'users' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">User Management</h3>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                      <input
                        type="text"
                        placeholder="Search users..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && fetchUsers()}
                        className="pl-10 pr-4 py-2 rounded-xl border bg-[#0F0F16] border-white/10 text-white placeholder-gray-600 outline-none focus:border-[#00F0FF] transition-all"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-[#08080C] rounded-2xl border border-white/10 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-[#0F0F16]">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">User</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {users.map((user) => (
                        <tr key={user.id} className="hover:bg-white/5">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00F0FF]/30 to-[#FFE800]/30 flex items-center justify-center text-white text-sm font-bold border border-white/10">
                                {user.name?.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="font-medium text-sm text-white">{user.name}</p>
                                {user.is_verified_streamer && (
                                  <span className="text-xs text-[#00F0FF]">✓ Verified</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-400">{user.email}</td>
                          <td className="px-4 py-3">
                            {user.is_banned ? (
                              <span className="px-2 py-1 rounded-full text-xs bg-[#FF2A2A]/10 text-[#FF2A2A] border border-[#FF2A2A]/20">Banned</span>
                            ) : user.is_admin ? (
                              <span className="px-2 py-1 rounded-full text-xs bg-[#FFE800]/10 text-[#FFE800] border border-[#FFE800]/20">Admin</span>
                            ) : (
                              <span className="px-2 py-1 rounded-full text-xs bg-[#00FF66]/10 text-[#00FF66] border border-[#00FF66]/20">Active</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleUserAction(user.id, 'is_verified_streamer', !user.is_verified_streamer)}
                                className={`p-1.5 rounded-lg transition-colors ${user.is_verified_streamer ? 'bg-[#00F0FF]/10 text-[#00F0FF]' : 'hover:bg-white/10 text-gray-400'}`}
                                title={user.is_verified_streamer ? 'Remove verification' : 'Verify'}
                              >
                                <UserCheck className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleUserAction(user.id, 'is_banned', !user.is_banned)}
                                className={`p-1.5 rounded-lg transition-colors ${user.is_banned ? 'bg-[#FF2A2A]/10 text-[#FF2A2A]' : 'hover:bg-white/10 text-gray-400'}`}
                                title={user.is_banned ? 'Unban' : 'Ban'}
                              >
                                <Ban className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'streams' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Stream Moderation</h3>
                
                <div className="space-y-3">
                  {streams.map((stream) => (
                    <div key={stream.id} className="p-4 rounded-xl bg-[#08080C] border border-white/10">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {stream.is_live && (
                            <span className="px-2 py-1 rounded-full text-xs bg-[#FF2A2A] text-white flex items-center gap-1">
                              <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                              LIVE
                            </span>
                          )}
                          <div>
                            <p className="font-medium text-white">{stream.title}</p>
                            <p className="text-sm text-gray-500">{stream.streamer_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-500">{stream.viewer_count || 0} viewers</span>
                          {stream.is_live && (
                            <button
                              onClick={() => handleTerminateStream(stream.id)}
                              className="px-3 py-1.5 rounded-lg bg-[#FF2A2A]/10 text-[#FF2A2A] text-sm font-medium hover:bg-[#FF2A2A]/20 transition-colors"
                            >
                              Terminate
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {streams.length === 0 && (
                    <div className="text-center py-12 text-gray-500">No streams found</div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'advertisers' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Advertiser Management</h3>
                
                <div className="space-y-3">
                  {advertisers.map((adv) => (
                    <div key={adv.id} className="p-4 rounded-xl bg-[#08080C] border border-white/10">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-white">{adv.company_name}</p>
                          <p className="text-sm text-gray-500">{adv.website} • {adv.industry}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 rounded-full text-xs border ${
                            adv.status === 'approved' ? 'bg-[#00FF66]/10 text-[#00FF66] border-[#00FF66]/20' :
                            adv.status === 'pending' ? 'bg-[#FFE800]/10 text-[#FFE800] border-[#FFE800]/20' :
                            'bg-[#FF2A2A]/10 text-[#FF2A2A] border-[#FF2A2A]/20'
                          }`}>
                            {adv.status}
                          </span>
                          {adv.status === 'pending' && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleAdvertiserStatus(adv.id, 'approved')}
                                className="p-2 rounded-lg bg-[#00FF66]/10 text-[#00FF66] hover:bg-[#00FF66]/20 transition-colors"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleAdvertiserStatus(adv.id, 'rejected')}
                                className="p-2 rounded-lg bg-[#FF2A2A]/10 text-[#FF2A2A] hover:bg-[#FF2A2A]/20 transition-colors"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'ads' && (
              <div className="space-y-4">
                <h3 className="text-base sm:text-lg font-semibold text-white">Ad Review</h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {ads.map((ad) => (
                    <div key={ad.id} className="p-4 rounded-xl bg-[#08080C] border border-white/10">
                      <div className="mb-3">
                        <p className="font-medium text-white">{ad.title}</p>
                        <p className="text-sm text-gray-500 line-clamp-2">{ad.description}</p>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className={`px-2 py-1 rounded-full text-xs border ${
                          ad.status === 'approved' ? 'bg-[#00FF66]/10 text-[#00FF66] border-[#00FF66]/20' :
                          ad.status === 'pending' ? 'bg-[#FFE800]/10 text-[#FFE800] border-[#FFE800]/20' :
                          'bg-[#FF2A2A]/10 text-[#FF2A2A] border-[#FF2A2A]/20'
                        }`}>
                          {ad.status}
                        </span>
                        {ad.status === 'pending' && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleAdStatus(ad.id, 'approved')}
                              className="p-2 rounded-lg bg-[#00FF66]/10 text-[#00FF66] hover:bg-[#00FF66]/20 transition-colors"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleAdStatus(ad.id, 'rejected')}
                              className="p-2 rounded-lg bg-[#FF2A2A]/10 text-[#FF2A2A] hover:bg-[#FF2A2A]/20 transition-colors"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reports' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Content Reports</h3>
                
                <div className="space-y-3">
                  {reports.map((report) => (
                    <div key={report.id} className="p-4 rounded-xl bg-[#08080C] border border-white/10">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="px-2 py-0.5 rounded text-xs bg-white/5 text-gray-400">{report.report_type}</span>
                            <span className="text-sm font-medium text-white">{report.reason}</span>
                          </div>
                          <p className="text-sm text-gray-500">{report.description}</p>
                        </div>
                        {report.status === 'pending' && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleResolveReport(report.id, 'action_taken')}
                              className="px-3 py-1.5 rounded-lg bg-[#FF2A2A]/10 text-[#FF2A2A] text-sm hover:bg-[#FF2A2A]/20 transition-colors"
                            >
                              Take Action
                            </button>
                            <button
                              onClick={() => handleResolveReport(report.id, 'dismissed')}
                              className="px-3 py-1.5 rounded-lg bg-white/5 text-gray-400 text-sm hover:bg-white/10 transition-colors"
                            >
                              Dismiss
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'verification' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Verification Applications</h3>
                
                <div className="space-y-3">
                  {verificationApps.map((app) => (
                    <div key={app.id} className="p-4 rounded-xl bg-[#08080C] border border-white/10">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-white">{app.user_name}</p>
                          <p className="text-sm text-gray-500 mb-2">{app.reason}</p>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span>{app.follower_count} followers</span>
                            <span>{app.total_streams} streams</span>
                          </div>
                        </div>
                        {app.status === 'pending' && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleVerification(app.id, true)}
                              className="px-3 py-1.5 rounded-lg bg-[#00FF66]/10 text-[#00FF66] text-sm font-medium hover:bg-[#00FF66]/20 transition-colors"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleVerification(app.id, false)}
                              className="px-3 py-1.5 rounded-lg bg-[#FF2A2A]/10 text-[#FF2A2A] text-sm font-medium hover:bg-[#FF2A2A]/20 transition-colors"
                            >
                              Reject
                            </button>
                          </div>
                        )}
                        {app.status !== 'pending' && (
                          <span className={`px-2 py-1 rounded-full text-xs border ${
                            app.status === 'approved' ? 'bg-[#00FF66]/10 text-[#00FF66] border-[#00FF66]/20' : 'bg-[#FF2A2A]/10 text-[#FF2A2A] border-[#FF2A2A]/20'
                          }`}>
                            {app.status}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {verificationApps.length === 0 && (
                    <div className="text-center py-12 text-gray-500">No verification applications</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
  <div className="p-5 rounded-2xl bg-[#08080C] border border-white/10">
    <div className="flex items-center justify-between mb-3">
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <p className="text-2xl font-bold mb-1 text-white">{value}</p>
    <p className="text-sm text-gray-500">{title}</p>
    {subtitle && <p className="text-xs mt-1" style={{ color }}>{subtitle}</p>}
  </div>
);

export default AdminPanel;
